import { useToast } from '@practium/ui';
import { useEffect, useRef, useState } from 'react';
import { useLocationOptions } from '../../analyses/hooks/useLocationOptions';
import { useProductsOptions } from '../../analyses/hooks/useProductsOptions';
import { usePostAdviceFoliar } from '../../api/hooks/recommendations/usePostAdviceFoliar';
import { useUpdateAdviceFoliar } from '../../api/hooks/recommendations/useUpdateAdviceFoliar';
import { DEFAULT_FOLIAR_FORM, DEFAULT_FOLIAR_PRODUCT } from '../constants/foliar.constants';
import { FoliarSchema } from '../schemas/foliar.schema';
import type { FoliarApplicationAdvice, FoliarFormData, FoliarProductFormData } from '../types';

export interface UseFoliarFormOptions {
	initialData?: FoliarFormData;
	adviceId?: string;
	onSubmit: (advice: FoliarApplicationAdvice) => void;
	sectorAreas?: Record<string, number>;
}

const computeProductResults = (
	dose: number,
	doseMeasurementUnit: '/ha' | '/100L' | '',
	tankVolume: number,
	syrupVolume: number,
	tanksToPrepare: number,
	sumArea: number
) => {
	if (!doseMeasurementUnit || dose <= 0 || tankVolume <= 0 || syrupVolume <= 0 || sumArea <= 0)
		return null;

	const totalSyrupVolume = syrupVolume * sumArea;
	const correction = totalSyrupVolume > tankVolume ? 1 : totalSyrupVolume / tankVolume;

	let quantityPerTank: number;
	let totalNecessary: number;

	if (doseMeasurementUnit === '/100L') {
		quantityPerTank = dose * (tankVolume / 100) * correction;
		totalNecessary = quantityPerTank * (tanksToPrepare > 0 ? tanksToPrepare : 1);
	} else {
		quantityPerTank = dose * (tankVolume / syrupVolume) * correction;
		totalNecessary = dose * sumArea;
	}

	if (correction !== 1) {
		totalNecessary = quantityPerTank;
	}

	return { quantityPerTank, totalNecessary };
};

export const useFoliarForm = ({ initialData, adviceId, onSubmit }: UseFoliarFormOptions) => {
	const toast = useToast();
	const initialForm = initialData
		? { ...DEFAULT_FOLIAR_FORM, ...initialData }
		: { ...DEFAULT_FOLIAR_FORM };
	const [form, setForm] = useState<FoliarFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const locationForm = {
		property: form.prop,
		area: form.area,
		sector: form.sectors,
	};

	const {
		propertyOptions: propOptions,
		areaOptions,
		sectorOptions,
		sectorAreas,
	} = useLocationOptions(locationForm);

	const { classProductOptions, getProductOptions } = useProductsOptions();

	const { mutateAsync: createFoliarAdvice, isPending: isLoading } = usePostAdviceFoliar();
	const { mutateAsync: updateFoliarAdvice, isPending: isUpdating } = useUpdateAdviceFoliar();
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (initialData) {
			const next = { ...DEFAULT_FOLIAR_FORM, ...initialData };
			setForm(next);
			initialFormRef.current = next;
			setErrors({});
		}
	}, [initialData]);

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const set = <K extends keyof any>(key: K, value: any[K]) => {
		setForm((f) => ({ ...f, [key]: value }));
		setErrors((e) => ({ ...e, [key as string]: '' }));
	};

	const setProduct = <K extends keyof FoliarProductFormData>(
		index: number,
		key: K,
		value: FoliarProductFormData[K]
	) => {
		setForm((f) => {
			const products = [...f.products];
			products[index] = { ...products[index], [key]: value };
			return { ...f, products };
		});
		setErrors((e) => ({ ...e, [`products.${index}.${key as string}`]: '' }));
	};

	const addProduct = () =>
		setForm((f) => ({ ...f, products: [...f.products, { ...DEFAULT_FOLIAR_PRODUCT }] }));

	const removeProduct = (index: number) => {
		if (form.products.length === 1) {
			toast.error('É necessário pelo menos um produto.');
			return;
		}
		setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== index) }));
	};

	const tankVolume = Number(form.tankVolume);
	const syrupVolume = Number(form.syrupVolume);
	const tanksToPrepare = Number(form.tanksToPrepare);
	const sumArea = form.sectors.reduce((acc, s) => acc + (sectorAreas[s] ?? 0), 0);

	const firstProduct = form.products[0];
	const computedResult = computeProductResults(
		Number(firstProduct?.dose),
		firstProduct?.doseMeasurementUnit ?? '/ha',
		tankVolume,
		syrupVolume,
		tanksToPrepare,
		sumArea
	);

	const handleSubmit = async () => {
		const result = FoliarSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = issue.path;
				if (path[0] === 'products' && path.length >= 3) {
					const key = `products.${String(path[1])}.${String(path[2])}`;
					fieldErrors[key] = issue.message;
				} else {
					const fieldName = String(path[0] ?? '');
					if (fieldName) fieldErrors[fieldName] = issue.message;
				}
			});

			setErrors(fieldErrors);
			return;
		}
		setErrors({});
		try {
			const tv = Number(form.tankVolume);
			const sv = Number(form.syrupVolume);
			const tt = Number(form.tanksToPrepare);
			const sa = form.sectors.reduce((acc, s) => acc + (sectorAreas[s] ?? 0), 0);

			const products = form.products.map((p) => {
				const dose = Number(p.dose);
				const unit = p.doseMeasurementUnit as '/ha' | '/100L';
				const r = computeProductResults(dose, unit, tv, sv, tt, sa);
				return {
					productClass: p.productClass,
					productCommercialName: p.productCommercialName,
					productTradeMark: p.productTradeMark || '',
					dose,
					doseMeasurementUnit: unit,
					quantityPerTank: r?.quantityPerTank || 0,
					totalNecessary: r?.totalNecessary || 0,
				};
			});

			const payload = {
				name: form.name,
				prop: form.prop,
				area: form.area,
				sectors: form.sectors,
				applicationDate: form.applicationDate,
				tankVolume: tv,
				syrupVolume: sv,
				tanksToPrepare: tt,
				products,
			};

			let saved: FoliarApplicationAdvice;

			if (adviceId) {
				await updateFoliarAdvice({ id: adviceId, data: payload });
				saved = { _id: adviceId, user: '', category: 'foliar', ...payload };
				toast.success('Recomendação atualizada com sucesso!');
			} else {
				await createFoliarAdvice(payload);
				saved = { user: '', category: 'foliar', ...payload };
				toast.success('Recomendação criada com sucesso!');
			}

			onSubmit(saved);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar recomendação');
		}
	};

	return {
		form,
		errors,
		isLoading: isLoading || isUpdating,
		computedResult,
		set,
		setProduct,
		addProduct,
		removeProduct,
		handleSubmit,
		isDirty,
		propOptions,
		areaOptions,
		sectorOptions,
		classProductOptions,
		getProductOptions,
	};
};
